/* Native Little Beast drawer. Shopify owns prices, discounts and cart state. */
(() => {
  'use strict';
  const TRIGGERS = '.btn-call-cart, [data-native-cart-open]';

  class LBNativeCart extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.queue = Promise.resolve();
      this.addRequests = new Map();
      this.panel = this.querySelector('.lb-cart-panel');
      this.overlay = this.querySelector('.lb-cart-overlay');
      this.status = this.querySelector('[data-cart-status]');
      this.error = this.querySelector('[data-cart-error]');
      this.root = window.Shopify?.routes?.root || '/';
      this.cart = this.readState(this);
      this.syncCount();
      this.prepareTriggers();
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
      this.addEventListener('change', event => {
        const input = event.target.closest('[data-cart-quantity]');
        if (!input) return;
        const quantity = Number(input.value);
        if (!Number.isInteger(quantity) || quantity < 0) {
          input.value = input.dataset.currentQuantity;
          this.showError('Please enter a whole number of items.');
          return;
        }
        this.change(input.closest('[data-line-key]').dataset.lineKey, quantity, input.id);
      });
      this.addEventListener('click', event => {
        if (this.panel.getAttribute('aria-busy') === 'true' && event.target.closest('.lb-cart-footer') && event.composedPath().some(node => node.matches?.('button'))) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, true);
      window.LittleBeastCart = this;
      this.installAppBridge();
    }

    readState(container) {
      return JSON.parse(container.querySelector('[data-cart-state]').textContent);
    }

    prepareTriggers() {
      document.querySelectorAll(TRIGGERS).forEach(trigger => {
        trigger.setAttribute('aria-controls', 'LBCartDialog');
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-expanded', String(this.isOpen));
        if (trigger.tagName !== 'BUTTON' && trigger.tagName !== 'A') {
          trigger.setAttribute('role', 'button');
          trigger.setAttribute('tabindex', '0');
        }
      });
    }

    get isOpen() { return !this.panel.hidden; }

    onDocumentClick(event) {
      const trigger = event.target.closest(TRIGGERS);
      if (trigger) {
        event.preventDefault();
        this.open(trigger);
        this.refresh().catch(error => this.showError(error.message));
        return;
      }
      if (!this.contains(event.target)) return;
      const close = event.target.closest('[data-cart-close]');
      if (close) { this.close(); return; }
      const row = event.target.closest('[data-line-key]');
      if (!row) return;
      const remove = event.target.closest('[data-cart-remove]');
      const step = event.target.closest('[data-cart-step]');
      if (!remove && !step) return;
      const input = row.querySelector('[data-cart-quantity]');
      const quantity = remove ? 0 : Math.max(0, Number(input.dataset.currentQuantity) + Number(step.dataset.cartStep));
      this.change(row.dataset.lineKey, quantity, input.id);
    }

    onKeydown(event) {
      if (!this.isOpen) {
        if ((event.key === 'Enter' || event.key === ' ') && event.target.matches(TRIGGERS) && event.target.tagName !== 'BUTTON' && event.target.tagName !== 'A') {
          event.preventDefault();
          event.target.click();
        }
        return;
      }
      if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
      if (event.key !== 'Tab') return;
      const focusable = [...this.panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex="0"]')]
        .filter(element => element.getClientRects().length && !element.closest('[hidden]'));
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first) { event.preventDefault(); this.panel.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || !this.panel.contains(document.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !this.panel.contains(document.activeElement))) {
        event.preventDefault(); first.focus();
      }
    }

    open(trigger) {
      if (!this.isOpen) {
        this.returnFocus = trigger || document.activeElement;
        this.panel.hidden = false;
        this.overlay.hidden = false;
        document.body.classList.add('lb-native-cart-open');
        this.background = document.querySelector('.inner-div');
        this.backgroundWasInert = this.background?.inert;
        if (this.background) this.background.inert = true;
        this.querySelector('[data-cart-close].lb-cart-close').focus();
        this.prepareTriggers();
        this.dispatchEvent(new CustomEvent('lb:cart:opened', { bubbles: true }));
      }
    }

    close() {
      this.panel.hidden = true;
      this.overlay.hidden = true;
      document.body.classList.remove('lb-native-cart-open');
      if (this.background) this.background.inert = this.backgroundWasInert;
      this.prepareTriggers();
      if (this.returnFocus?.isConnected) this.returnFocus.focus();
    }

    showError(message) {
      this.error.textContent = message || 'Something went wrong. Please try again.';
      this.error.hidden = false;
    }

    setBusy(busy) {
      this.panel.setAttribute('aria-busy', String(busy));
      this.querySelectorAll('[data-cart-step], [data-cart-remove], [data-cart-quantity], [data-cart-checkout]').forEach(element => { element.disabled = busy; });
      if (!busy && this.nextFocus && this.isOpen) {
        const next = this.querySelector('#' + CSS.escape(this.nextFocus)) || this.querySelector('.lb-cart-close');
        next.focus();
        this.nextFocus = null;
      }
    }

    enqueue(task) {
      const result = this.queue.then(async () => {
        this.error.hidden = true;
        this.setBusy(true);
        try { return await task(); }
        finally { this.setBusy(false); }
      });
      this.queue = result.catch(() => {});
      return result;
    }

    async fetchJSON(url, options) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { ...options, signal: controller.signal });
        return { response, data: await response.json() };
      } catch (error) {
        if (error.name === 'AbortError') throw new Error('Your cart is taking longer than expected. Please try again.');
        throw error;
      } finally { clearTimeout(timeout); }
    }

    async request(path, body) {
      const url = new URL(this.root + path, location.origin);
      const { response, data: state } = await this.fetchJSON(url.toString(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: [this.dataset.sectionId], sections_url: this.root })
      });
      if (!response.ok || state.status) throw new Error(typeof state.description === 'string' ? state.description : 'Unable to update your cart. Please try again.');
      return state;
    }

    async render(state, focusId) {
      const html = state.sections?.[this.dataset.sectionId];
      if (!html) { await this.fetchContents(focusId); return; }
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const content = parsed.querySelector('[data-cart-content]');
      if (!content) throw new Error('Unable to display your cart. Please refresh the page.');
      const current = this.querySelector('[data-cart-content]');
      // Keep app-owned upsell DOM connected; only Shopify cart fragments change.
      for (const selector of ['[data-cart-state]', '.lb-cart-body', '.lb-cart-footer']) {
        current.querySelector(selector).replaceWith(content.querySelector(selector));
      }
      this.cart = this.readState(current);
      this.querySelector('[data-cart-upsell]').hidden = this.cart.item_count === 0;
      this.syncCount();
      this.setBusy(this.panel.getAttribute('aria-busy') === 'true');
      this.status.textContent = `Cart updated. ${this.cart.item_count} ${this.cart.item_count === 1 ? 'item' : 'items'}.`;
      if (focusId) this.nextFocus = focusId;
      this.dispatchEvent(new CustomEvent('lb:cart:updated', { bubbles: true, detail: { cart: this.cart } }));
    }

    syncCount() {
      const count = this.cart.item_count;
      document.querySelectorAll('[id="cartIconCount"], [id="cartIconCountMob"]').forEach(trigger => {
        const countElement = trigger.querySelector('span');
        if (countElement) countElement.textContent = count;
        trigger.setAttribute('aria-label', `Cart, ${count} ${count === 1 ? 'item' : 'items'}`);
      });
    }

    async fetchContents(focusId) {
      const url = new URL(this.root, window.location.origin);
      url.searchParams.set('sections', this.dataset.sectionId);
      if (this.dataset.previewThemeId) url.searchParams.set('preview_theme_id', this.dataset.previewThemeId);
      const { response, data: sections } = await this.fetchJSON(url.toString(), { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Unable to load your cart. Please try again.');
      if (!sections[this.dataset.sectionId]) throw new Error('Unable to display your cart. Please refresh the page.');
      await this.render({ sections }, focusId);
    }

    refresh() { return this.enqueue(async () => { await this.fetchContents(); await this.ensureFinalSale(); }); }

    async ensureFinalSale() {
      // App add-ons bypass the theme's add-to-cart handler. Preserve every existing
      // property while adding the same Final Sale marker used by PDP/quick-add.
      for (let remaining = this.cart.items.length; remaining > 0; remaining--) {
        const keys = [...this.querySelectorAll('[data-line-key][data-final-sale="true"]')].map(row => row.dataset.lineKey);
        const item = this.cart.items.find(item => keys.includes(item.key) && !item.properties?.['Final Sale']);
        if (!item) break;
        const state = await this.request('cart/change.js', {
          id: item.key,
          quantity: item.quantity,
          properties: { ...item.properties, 'Final Sale': 'Yes' }
        });
        await this.render(state);
      }
    }

    add(data) {
      const signature = JSON.stringify(data);
      if (this.addRequests.has(signature)) return this.addRequests.get(signature);
      const trigger = document.activeElement;
      const pending = this.enqueue(async () => {
        const state = await this.request('cart/add.js', data);
        await this.render(state);
        await this.ensureFinalSale();
        this.open(trigger);
        return state;
      }).catch(error => { this.open(trigger); this.showError(error.message); throw error; })
        .finally(() => this.addRequests.delete(signature));
      this.addRequests.set(signature, pending);
      return pending;
    }

    change(key, quantity, focusId) {
      return this.enqueue(async () => {
        const state = await this.request('cart/change.js', { id: key, quantity });
        await this.render(state, focusId);
      }).catch(error => {
        const input = focusId && this.querySelector('#' + CSS.escape(focusId));
        if (input) input.value = input.dataset.currentQuantity;
        this.showError(error.message);
      });
    }

    installAppBridge() {
      // Event emitted by the installed Selleasy version after cart mutations.
      // It also observes Checkout+ and other apps. Avoid another global fetch patch.
      window.addEventListener('lbSECartUpdated', event => {
        if (!event.detail?.cart || this.fingerprint(event.detail.cart) === this.fingerprint(this.cart)) return;
        this.scheduleAppRefresh(event.detail.isAtc);
      });
    }

    fingerprint(cart) {
      return JSON.stringify([cart.total_price, cart.item_count,
        cart.items.map(item => [item.key, item.quantity, item.final_line_price]).sort((a, b) => a[0].localeCompare(b[0]))]);
    }

    scheduleAppRefresh(open) {
      this.appShouldOpen = this.appShouldOpen || open;
      clearTimeout(this.appTimer);
      this.appTimer = setTimeout(() => {
        const shouldOpen = this.appShouldOpen;
        this.appShouldOpen = false;
        this.refresh().then(() => { if (shouldOpen) this.open(); }).catch(error => this.showError(error.message));
      }, 100);
    }
  }

  if (!customElements.get('lb-native-cart')) customElements.define('lb-native-cart', LBNativeCart);
})();
